import { Home, User, BookOpen, Heart, History, MessageSquare, LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  currentPage?: string;
  onNavigate?: (page: string) => void;
  isLoggedIn?: boolean;
  onLogout?: () => void;
}

const menuItems = [
  { id: "home", label: "首页", sublabel: "ホーム", icon: Home },
  { id: "profile", label: "个人中心", sublabel: "プロフィール", icon: User },
  { id: "vocabulary", label: "生词本", sublabel: "単語帳", icon: BookOpen },
  { id: "favorites", label: "我的收藏", sublabel: "お気に入り", icon: Heart },
  { id: "history", label: "学习记录", sublabel: "履歴", icon: History },
  { id: "feedback", label: "意见反馈", sublabel: "ご意見", icon: MessageSquare },
];

export function Sidebar({ 
  isCollapsed, 
  onToggle, 
  currentPage = "home",
  onNavigate,
  isLoggedIn,
  onLogout
}: SidebarProps) {
  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300",
        "hidden lg:block", // Hide on mobile
        isCollapsed ? "w-16" : "w-60"
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
          {!isCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-hero flex items-center justify-center shadow-lg">
                <span className="text-primary-foreground font-bold text-lg">🍡</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-base text-sidebar-foreground leading-tight">
                  dango
                </span>
                <span className="text-[10px] text-sidebar-foreground/60">
                  用耳朵学日语
                </span>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-8 w-8 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4 scrollbar-zen">
          <nav className="space-y-1 px-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;
              
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-3 h-11 text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent",
                    isCollapsed && "justify-center px-2",
                    isActive && "bg-sidebar-accent text-sidebar-primary font-medium"
                  )}
                  onClick={() => onNavigate?.(item.id)}
                >
                  <Icon className={cn("h-5 w-5 shrink-0", isActive && "text-sidebar-primary")} />
                  {!isCollapsed && (
                    <div className="flex flex-col items-start">
                      <span className="text-sm leading-tight">{item.label}</span>
                      <span className="text-[10px] opacity-60">{item.sublabel}</span>
                    </div>
                  )}
                </Button>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Footer */}
        <div className="border-t border-sidebar-border p-2 space-y-1">
          {isLoggedIn && (
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start gap-3 h-10 text-sidebar-foreground/60 hover:text-destructive hover:bg-sidebar-accent",
                isCollapsed && "justify-center px-2"
              )}
              onClick={onLogout}
            >
              <LogOut className="h-5 w-5 shrink-0" />
              {!isCollapsed && <span className="text-sm">退出登录</span>}
            </Button>
          )}
          {!isCollapsed && (
            <p className="text-[10px] text-sidebar-foreground/50 text-center py-2">
              客服VX：shunlixiezuo168
            </p>
          )}
        </div>
      </div>
    </aside>
  );
}
